import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {equals} from 'ramda'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {removeOnAuthSuccessLandingUrl} from '@vio/core/src/auth/business/persistData'
import {updateUser} from '@vio/core/src/auth/components/FetchUser'
import {
  userHasJustAuthenticated,
  userSuccessfullyAuthenticatedWithEmail
} from '@vio/core/src/auth/modules/actions'
import {
  endPreAuthAttempt,
  persistAuthJustSucceeded,
  startLoginSignUp,
  verifyOneTimePassword
} from '@vio/core/src/auth/services'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'

import {clearFreezeIntent} from '../../dealFreeze/utils/freezeIntent'
import {clearDealFreezeStash} from '../../dealFreeze/utils/stash'
import {setEmailSignUpVisibility} from './slice'
import {
  getEmailSignInAnalyticsData,
  getEmailSignInFlow,
  getEmailSignInSourceComponent,
  getIsEmailSignInOpen
} from './slice/selectors'
import {
  trackErrorOnRequestOneTimePassword,
  trackErrorOnVerifyOneTimePassword,
  trackOnClickOneTimePassword,
  trackOnClickVerifyOneTimePassword,
  trackSuccessfulAuthentication,
  trackValidationError
} from './trackingEvents'

export interface FormData {
  emailAddress: string
}

type EmailSignUpContextType = {
  isOpen: boolean
  onClose: () => void
  isSecondStep: boolean
  emailAddress: string
  setIsSecondStep: (value: boolean) => void
  handleValidateOneTimePassword: (oneTimePassword: string) => void
  oneTimePasswordError: boolean
  oneTimePassword: string
  setOneTimePassword: (value: string) => void
  setOneTimePasswordError: (value: boolean) => void
  setEmailAddress: (value: string) => void
  requestOneTimePassword: (emailAddress: string) => void
  shouldEnableSend: boolean
  isSubmitting: boolean
  isValidating: boolean
  shouldCountTime: boolean
  secondsRemaining: number
  hasSubmitError: boolean
  setUnSubmittedEmailAddress: (value: string) => void
  handleSubmitEmailAddress: (formData: FormData) => void
  handleValidationError: () => void
}

type Props = {
  children: ReactNode
  apiUrl: string
  languageCode: string
}

export const RESEND_WAITING_TIME = 30

const EmailSignUpContext = createContext<EmailSignUpContextType | undefined>(
  undefined
)

export const useEmailSignUpContext = (): EmailSignUpContextType => {
  const context = useContext(EmailSignUpContext)
  if (!context) {
    throw new Error(
      'useEmailSignUpContext must be used within an EmailSignUpProvider'
    )
  }
  return context
}

export const EmailSignUpProvider = ({
  children,
  apiUrl,
  languageCode
}: Props) => {
  const dispatch = useDispatch()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isOpen = useSelector(getIsEmailSignInOpen)
  const sourceComponent =
    useSelector(getEmailSignInSourceComponent) || undefined
  const flow = useSelector(getEmailSignInFlow)
  const analyticsData = useSelector(getEmailSignInAnalyticsData)
  const [hasSubmitError, setHasSubmitError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [oneTimePasswordError, setOneTimePasswordError] = useState(false)
  const [oneTimePassword, setOneTimePassword] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [isSecondStep, setIsSecondStep] = useState(false)
  const [shouldCountTime, setShouldCountTime] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(RESEND_WAITING_TIME)
  const [loginSession, setLoginSession] = useState<{
    session: string
  } | null>(null)
  const [shouldEnableSend, setShouldEnableSend] = useState(true)
  const [unSubmittedEmailAddress, setUnSubmittedEmailAddress] = useState('')

  const {brandCode} = useBrand()

  const requestOneTimePassword = async (emailAddress: string) => {
    try {
      setIsSubmitting(true)

      trackOnClickOneTimePassword(sourceComponent, dispatch, analyticsData)

      const {session} = await startLoginSignUp(
        emailAddress,
        brandCode,
        languageCode,
        apiUrl,
        sourceComponent as SourceComponentType,
        flow ?? null
      )

      setLoginSession({session})
      setOneTimePasswordError(false)
      setShouldCountTime(true)
      setIsSecondStep(true)
      setHasSubmitError(false)
    } catch (error: unknown) {
      trackErrorOnRequestOneTimePassword(
        sourceComponent,
        flow ?? null,
        error as string,
        dispatch
      )

      setHasSubmitError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleValidateOneTimePassword = async (oneTimePassword: string) => {
    try {
      setIsValidating(true)

      trackOnClickVerifyOneTimePassword(
        sourceComponent,
        dispatch,
        analyticsData
      )

      if (!loginSession?.session) {
        setOneTimePasswordError(true)
        setIsValidating(false)

        trackErrorOnVerifyOneTimePassword(
          sourceComponent,
          flow ?? null,
          'OTPAuthentication',
          'Missing session',
          dispatch
        )

        return
      }

      await verifyOneTimePassword(
        emailAddress,
        oneTimePassword,
        loginSession?.session,
        apiUrl
      )

      setIsValidating(false)

      // fetch user and set user state
      await updateUser({
        publicUrl: null,
        dispatch,
        skipLoadingState: true
      })

      // track user authentication
      trackSuccessfulAuthentication(sourceComponent, dispatch, analyticsData)
      endPreAuthAttempt()

      persistAuthJustSucceeded()
      dispatch(userHasJustAuthenticated())

      dispatch(userSuccessfullyAuthenticatedWithEmail())

      closeEmailSignUp({keepDealFreezeResume: true})
    } catch (error: unknown) {
      const errorWithSession = error as unknown as {
        response: {data: {session: string}; status: number}
      }

      // When the user enters the wrong OTP, the session is returned in the error response so we can allow them to try again
      if (errorWithSession?.response?.data?.session) {
        setLoginSession({
          session: errorWithSession.response.data.session
        })
      }

      if (errorWithSession?.response?.status === 422) {
        trackErrorOnVerifyOneTimePassword(
          sourceComponent,
          flow ?? null,
          'OTPAuthentication',
          'Invalid OTP',
          dispatch
        )
        return
      }

      // Unknown error
      trackErrorOnVerifyOneTimePassword(
        sourceComponent,
        flow ?? null,
        'OTPAuthentication',
        JSON.stringify(errorWithSession?.response?.data),
        dispatch
      )
    } finally {
      setIsValidating(false)
      setOneTimePasswordError(true)
    }
  }

  const handleSubmitEmailAddress = (formData: FormData) => {
    setEmailAddress(formData.emailAddress)
    requestOneTimePassword(formData.emailAddress)
  }

  const handleValidationError = () => {
    trackValidationError(
      sourceComponent,
      flow ?? null,
      'emailValidation',
      'Invalid email address',
      dispatch
    )
  }

  const closeEmailSignUp = (options?: {keepDealFreezeResume?: boolean}) => {
    endPreAuthAttempt()
    // Cleared on abandon, or the next unrelated sign-in would land on the freeze sheet.
    if (
      !options?.keepDealFreezeResume &&
      flow === AuthenticationFlows.DealFreeze
    ) {
      clearDealFreezeStash()
      clearFreezeIntent()
      removeOnAuthSuccessLandingUrl()
    }
    dispatch(
      setEmailSignUpVisibility({
        isOpen: false,
        sourceComponent: undefined,
        flow: undefined,
        analyticsData: undefined
      })
    )
    setIsSecondStep(false)
    setHasSubmitError(false)
  }

  // Takes no argument: it is wired straight to close controls, which pass an event.
  const onClose = () => closeEmailSignUp()

  const resetWaitingTime = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
    intervalRef.current = null
    setShouldEnableSend(true)
    setShouldCountTime(false)
    setSecondsRemaining(RESEND_WAITING_TIME)
  }

  useEffect(() => {
    if (!shouldCountTime) return

    setShouldEnableSend(false)

    intervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => prev - 1)
    }, 1000)

    if (equals(secondsRemaining, 0)) {
      resetWaitingTime()
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [secondsRemaining, shouldCountTime])

  useEffect(() => {
    if (emailAddress !== unSubmittedEmailAddress) {
      resetWaitingTime()
    }
  }, [emailAddress, unSubmittedEmailAddress])

  return (
    <EmailSignUpContext.Provider
      value={{
        isOpen,
        onClose,
        isSecondStep,
        emailAddress,
        setIsSecondStep,
        oneTimePasswordError,
        setOneTimePasswordError,
        setOneTimePassword,
        oneTimePassword,
        setEmailAddress,
        requestOneTimePassword,
        shouldEnableSend,
        isSubmitting,
        isValidating,
        shouldCountTime,
        secondsRemaining,
        hasSubmitError,
        handleValidateOneTimePassword,
        setUnSubmittedEmailAddress,
        handleSubmitEmailAddress,
        handleValidationError
      }}
    >
      {children}
    </EmailSignUpContext.Provider>
  )
}
